import React, { useState } from "react"
import { DeviceHistoryControls } from "./DeviceHistoryControls"
import { LineChart } from "../Charts/LineChart"
import { BooleanChart } from "../Charts/BooleanChart"
import { BarChart } from "../Charts/BarChart"
import { ChartList } from "../Charts/ChartList"
import { useRole } from "../RoleContext"
import { Col, Form, Row } from "react-bootstrap"
import { Permission } from "../../api"

export const SystemGraphs = ({
    id,
    devices,
    isDevice = false,
    includeAnalytics = true,
    sn = undefined,
}) => {
    const { permissions } = useRole()
    const analyticsAllowed = permissions.includes(Permission.GetAnyResourceDeviceAnalytics)
    const barResolutions = ["hour", "day", "week", "month", "year"]
    const [barRes, setBarRes] = useState(barResolutions[0])

    const Controls = () => <DeviceHistoryControls className="mb-3" />

    const BarChartResolutionSelector = () => (
        <Form.Group as={Row} className="mb-3">
            <Form.Label column sm="2">
                Bar chart resolution
            </Form.Label>
            <Col sm="10">
                <Form.Select
                    value={barRes}
                    onChange={(e) => {
                        setBarRes(e.target.value)
                    }}
                >
                    {barResolutions.map((res) => (
                        <option key={res}>{res}</option>
                    ))}
                </Form.Select>
            </Col>
        </Form.Group>
    )

    return (
        <ChartList
            id={id}
            barChartResolution={isDevice ? "" : barRes}
            isDevice={isDevice}
            devices={devices}
        >
            <Controls />

            <h2>Ambient</h2>
            <LineChart
                chartId="ambient"
                chartIdentifier={sn !== undefined ? sn : id}
                values={[
                    { key: "ambient_eco2", hide: true },
                    { key: "ambient_humidity", hide: true },
                    "ambient_temperature",
                    { key: "room_setpoint", type: "event" },
                ]}
                baseValue={{
                    type: "continuous",
                }}
            />

            <Controls />

            <h2>Air</h2>
            <LineChart
                chartId="air"
                chartIdentifier={sn !== undefined ? sn : id}
                values={[
                    "air_outdoor_temperature",
                    "air_exhaust_temperature",
                    "air_flow_setpoint",
                    "air_ducts_position",
                    "air_external_temperature",
                    "air_indoor_humidity",
                    "air_return_temperature",
                    "air_fan_speed",
                    "air_ventilation_flow",
                    "air_thermal_resistance",
                    "heat_pump_air_exhaust_temperature",
                    "heat_pump_determined_air_fan_speed_setpoint",
                    "external_differential_pressure",
                    "heat_pump_determined_air_indoor_valve_position",
                    "heat_pump_determined_air_outdoor_valve_position",
                    "ventilation_requested_auto_demand",
                    "pure_air_air_in_temperature",
                    "pure_air_air_out_temperature",
                    "pure_air_fan_speed",
                    "pure_air_determined_fan_speed_setpoint",
                ]}
                baseValue={{
                    type: "continuous",
                }}
            />

            <Controls />

            <h2>Water</h2>
            <LineChart
                chartId="water"
                chartIdentifier={sn !== undefined ? sn : id}
                values={[
                    "water_return_temperature",
                    "water_supply_temperature",
                    "central_heating_water_pressure",
                    "water_condenser_water_out_temperature",
                    "water_flow",
                    "heat_pump_determined_water_pump_speed_setpoint",
                    "external_water_target_temperature",
                    "external_degree_minutes",
                    { key: "heat_pump_auxiliary_heating_degree_minutes", hide: true },
                    "heat_pump_determined_central_heating_supply_temperature",
                    "pure_air_estimated_water_valve_position",
                    { key: "boiler_ntc_1_temperature", hide: true },
                    { key: "boiler_ntc_2_temperature", hide: true },
                    { key: "boiler_ntc_3_temperature", hide: true },
                    { key: "boiler_ntc_4_temperature", hide: true },
                ]}
                baseValue={{
                    type: "continuous",
                }}
            />

            <Controls />

            <h2>Heat pump</h2>
            <LineChart
                chartId="heatpump"
                chartIdentifier={sn !== undefined ? sn : id}
                values={[
                    "heat_pump_hot_gas_temperature",
                    "heat_pump_condenser_refrigerant_out_temperature",
                    "heat_pump_evaporator_refrigerant_out_temperature",
                    "heat_pump_evaporation_pressure",
                    "heat_pump_condensation_pressure",
                    "heat_pump_current_compressor_speed",
                    "heat_pump_desired_compressor_speed",
                    "heat_pump_determined_compressor_speed",
                    "heat_pump_condensation_temperature",
                    "heat_pump_evaporation_temperature",
                    "heat_pump_determined_superheat_expansion_valve_position",
                    "external_defrost_value",
                    "external_defrost_threshold",
                    "heat_pump_defrost_freezing_value",
                    "heat_pump_defrost_freezing_value_threshold",
                ]}
                baseValue={{
                    type: "continuous",
                }}
            />

            <Controls />

            <h2>Status</h2>
            <BooleanChart
                chartId="status"
                chartIdentifier={sn !== undefined ? sn : id}
                showInts={true}
                values={[
                    "ventilation_level",
                    "flame_state",
                    "connected",
                    "heat_recovery_valve_central_heating_active",
                    "heat_pump_desired_reverse_cycle_position_is_cooling",
                    "boiler_dhw_valve_state",
                    "thermostat_hybrid_ch_enable",
                    "reverse_cycle_position_is_cooling",
                    "climate_mode",
                    //"boiler_valve_zone_1_state",
                    //"boiler_valve_zone_2_state",
                    //"boiler_valve_zone_3_state",
                ]}
                baseValue={{
                    type: "event",
                }}
            />

            <Controls />

            <h2>Auxillary heating</h2>
            <BooleanChart
                chartId="auxillary-heating"
                chartIdentifier={sn !== undefined ? sn : id}
                values={[
                    "backup_heater_1_active",
                    "backup_heater_2_active",
                    "backup_heater_3_active",
                    "central_heating_enabled",
                ]}
                baseValue={{
                    type: "event",
                }}
            />

            {includeAnalytics && analyticsAllowed && (
                <>
                    <Controls />

                    <h2>Analytics temperature & flow</h2>
                    <LineChart
                        chartId="analytics-temperature-and-flow"
                        chartIdentifier={sn !== undefined ? sn : id}
                        values={[
                            "external_total_supply_temperature",
                            "external_total_return_temperature",
                            "external_aux_supply_temperature",
                            "external_aux_return_temperature",
                            "external_total_flow",
                            "external_aux_flow",
                        ]}
                        baseValue={{
                            type: "continuous",
                        }}
                    />

                    <Controls />

                    <h2>Analytics electric</h2>
                    <LineChart
                        chartId="analytics-electric"
                        chartIdentifier={sn !== undefined ? sn : id}
                        values={[
                            "external_voltage_l1",
                            "external_current_l1",
                            "external_voltage_l2",
                            "external_current_l2",
                            "external_voltage_l3",
                            "external_current_l3",
                            { key: "external_current_n", hide: true },
                        ]}
                        baseValue={{
                            type: "continuous",
                        }}
                    />

                    <Controls />

                    <h2>Analytics power</h2>
                    <LineChart
                        chartId="analytics-power"
                        chartIdentifier={sn !== undefined ? sn : id}
                        values={[
                            "external_electric_power_l1",
                            { key: "external_electric_power_l2", hide: true },
                            { key: "external_electric_power_l3", hide: true },
                            { key: "external_electric_power_total", hide: true },
                            "external_total_power",
                            "external_aux_power",
                            "calculated_central_heating_heat_pump_power",
                            "calculated_central_heating_aux_power",
                            "calculated_central_heating_aux_power_calculated",
                            "calculated_domestic_hot_water_heat_pump_power",
                            "calculated_domestic_hot_water_aux_power",
                            "calculated_total_power_calculated",
                        ]}
                        baseValue={{
                            type: "continuous",
                        }}
                    />

                    <Controls />

                    <h2>Analytics energy sensors</h2>
                    <LineChart
                        chartId="analytics-energy-sensors"
                        chartIdentifier={sn !== undefined ? sn : id}
                        values={[
                            "external_aux_energy",
                            "external_electric_energy_l1",
                            "external_electric_energy_l2",
                            "external_electric_energy_l3",
                            "external_electric_energy_total",
                            "external_total_heat_energy",
                            { key: "external_central_heating_aux_energy_delta", hide: true },
                            { key: "external_central_heating_heat_pump_energy_delta", hide: true },
                            { key: "external_domestic_hot_water_aux_energy_delta", hide: true },
                            {
                                key: "external_domestic_hot_water_heat_pump_energy_delta",
                                hide: true,
                            },
                            { key: "external_energy_delta_total", hide: true },
                        ]}
                        baseValue={{
                            type: "continuous",
                        }}
                    />

                    <Controls />
                    <hr />
                    <BarChartResolutionSelector />

                    <h2>Analytics energy</h2>
                    <BarChart
                        chartId="analytics-energy"
                        chartIdentifier={sn !== undefined ? sn : id}
                        values={[
                            { key: "calculated_electric_energy_l1", hide: true },
                            { key: "calculated_electric_energy_l2", hide: true },
                            { key: "calculated_electric_energy_l3", hide: true },
                            "calculated_electric_energy_total",
                            "calculated_aux_heat_energy",
                            "calculated_heat_pump_heat_energy",
                            "calculated_central_heating_aux_energy_calculated",
                            "calculated_total_heat_energy",
                            "calculated_total_energy_calculated",
                            "calculated_central_heating_heat_pump_energy",
                            "calculated_domestic_hot_water_aux_energy",
                            "calculated_domestic_hot_water_aux_energy_calculated",
                            "calculated_domestic_hot_water_heat_pump_energy",
                        ]}
                        baseValue={{
                            type: "bar",
                        }}
                    />

                    <Controls />

                    <h2>Analytics COP</h2>
                    <BarChart
                        chartId="analytics-cop"
                        chartIdentifier={sn !== undefined ? sn : id}
                        values={["calculated_cop_heat_pump", "calculated_cop_total"]}
                        baseValue={{
                            type: "bar",
                        }}
                    />
                </>
            )}
        </ChartList>
    )
}

// Not included
// "air_as200_sensor_state",
// "external__domestic_hot_water_aux_energy_delta",
// "heat_pump_desired_air_indoor_valve_setpoint",
// "heat_pump_desired_air_outdoor_valve_setpoint",
// "heat_pump_desired_compressor_speed",
// "heat_pump_desired_reverse_cycle_position_is_cooling",
// "heat_pump_determined_superheat_expansion_valve_position",
// "heat_pump_inverter_fault_code",
// "heat_pump_inverter_input_power",
// "heat_pump_maximum_compressor_speed_envelope",
// "heat_pump_maximum_compressor_speed_hotgas",
// "heat_pump_minimum_compressor_speed_envelope",
// "performance_energy_consumption_l1",
// "performance_energy_consumption_l2",
// "performance_energy_consumption_l3",
// "performance_power_usage_l1",
// "performance_power_usage_l2",
// "performance_power_usage_l3",
// "water_pump_status",
